import React, { useEffect, useState } from 'react';
import { Archive, Loader2, Download, FileText, Landmark, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { lastFinancialYear, currentFinancialYear, fyLabel } from '../lib/financialYear';

const money = (v) => `$${Number(v || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const card = { borderColor: 'rgba(0,0,0,0.08)' };

// AGM pack: year-end Treasurer's report + a statement per account + member counts, downloaded as one ZIP.
export function AgmPackCard() {
  const { http } = useAuth();
  const [[from, to], setRange] = useState(lastFinancialYear());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!from || !to) return;
    setBusy(true);
    http.get(`/admin/agm/pack/preview?from_date=${from}&to_date=${to}`).then((r) => setPreview(r.data)).catch(() => setPreview(null)).finally(() => setBusy(false));
  }, [from, to, http]);

  const download = () => {
    if (!from || !to) return toast.error('Choose the year to bundle.');
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/agm/pack?from_date=${from}&to_date=${to}`, '_blank');
    toast.success('Building the AGM pack — your download will start shortly.');
  };

  const chip = (label, range, active, testid) => (
    <button type="button" onClick={() => setRange(range)} className="text-[11px] px-2 py-1 rounded-full" data-testid={testid}
      style={{ background: active ? 'var(--maroon)' : 'rgba(0,0,0,0.06)', color: active ? 'var(--cream)' : 'var(--navy)' }}>{label}</button>
  );
  const lastFy = lastFinancialYear(); const curFy = currentFinancialYear();

  return (
    <div className="bg-white rounded-lg p-5 border shadow-sm" style={card} data-testid="agm-pack-card">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--navy)' }}><Archive size={18} /> AGM report pack</h3>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex gap-1 mr-1" data-testid="agm-presets">
            {chip(`FY ${fyLabel(lastFy)}`, lastFy, from === lastFy[0] && to === lastFy[1], 'agm-preset-last-fy')}
            {chip(`FY ${fyLabel(curFy)} to date`, [curFy[0], new Date().toISOString().slice(0, 10)], from === curFy[0], 'agm-preset-this-fy')}
          </div>
          <label className="text-xs" style={{ color: 'var(--olive)' }}>From<br /><Input type="date" value={from} onChange={(e) => setRange([e.target.value, to])} className="h-9 w-40" data-testid="agm-from" /></label>
          <label className="text-xs" style={{ color: 'var(--olive)' }}>To<br /><Input type="date" value={to} onChange={(e) => setRange([from, e.target.value])} className="h-9 w-40" data-testid="agm-to" /></label>
          <button onClick={download} disabled={busy || !preview} data-testid="agm-download-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy || !preview ? 0.7 : 1 }}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />} Download ZIP
          </button>
        </div>
      </div>
      <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>One ZIP for the AGM: the year-end Treasurer's report, a statement (PDF + CSV) for every account, and member counts. Defaults to the 1 Nov – 31 Oct year just past.</p>
      {preview && (
        <div className="mt-3" data-testid="agm-preview">
          <button type="button" onClick={() => setShowPreview((v) => !v)} className="text-xs underline" style={{ color: 'var(--navy)' }} data-testid="agm-preview-toggle">
            {showPreview ? 'Hide' : 'Show'} what's inside ({preview.files} files)
          </button>
          {showPreview && (
            <div className="grid sm:grid-cols-3 gap-3 mt-2 text-sm">
              <div className="rounded border p-3" style={card} data-testid="agm-preview-treasurer">
                <div className="flex items-center gap-1 font-title text-xs mb-1" style={{ color: 'var(--maroon)' }}><FileText size={13} /> Treasurer's report</div>
                <div>Opening {money(preview.treasurer.balance_start_total)}</div>
                <div>Income {money(preview.treasurer.total_income)} · Spent {money(preview.treasurer.total_expenditure)}</div>
                <div className="font-title">Closing {money(preview.treasurer.balance_end_total)}</div>
              </div>
              <div className="rounded border p-3" style={card} data-testid="agm-preview-accounts">
                <div className="flex items-center gap-1 font-title text-xs mb-1" style={{ color: 'var(--maroon)' }}><Landmark size={13} /> Statements ({preview.accounts.length} accounts)</div>
                {preview.accounts.length ? preview.accounts.map((a) => <div key={a.id} className="flex justify-between text-xs"><span>{a.name}</span><span>{money(a.balance)}</span></div>) : <div className="text-xs" style={{ color: 'var(--olive)' }}>No accounts yet.</div>}
              </div>
              <div className="rounded border p-3" style={card} data-testid="agm-preview-members">
                <div className="flex items-center gap-1 font-title text-xs mb-1" style={{ color: 'var(--maroon)' }}><Users size={13} /> Members</div>
                <div>{preview.members.active_financial} financial · {preview.members.total} on register</div>
                <div className="text-xs" style={{ color: 'var(--olive)' }}>+{preview.members.joined_in_period} joined, {preview.members.lapsed_in_period} lapsed this year</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
